/* Changing a cart line's size in place.

   F&S sizes are line-item properties rather than variants, so without this the
   only way to correct one is to remove the piece and add it again.

   The editor markup is the product page's own size control, and this file only
   moves the answer to the cart: `fs-size-selector.js` initialises the control and
   reports through `fs:size-change`, so the rules about ranges, custom
   measurements and which options exist stay in one place.

   Two things the Ajax API forces:

   1. `properties` on cart/change.js overwrites the whole object, so the line's
      other properties have to be read back and sent with it. Sending only the
      size would silently drop everything else the line carries.
   2. Lines are addressed by key rather than index, because an index moves when
      another line is removed and would then rewrite the wrong piece. */
(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';

  const setStatus = (block, text) => {
    const status = block.querySelector('[data-fs-cart-size-status]');
    if (status) status.textContent = text;
  };

  async function applySize(block, value) {
    const key = block.dataset.fsSizeKey;
    const lineKey = block.dataset.fsLineKey;
    if (!key || !lineKey || !value) return;

    const shown = block.querySelector('[data-fs-cart-size-value]');
    if (shown && shown.textContent.trim() === value) return;

    setStatus(block, 'Updating…');
    try {
      const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })).json();
      const line = cart.items.find((item) => item.key === lineKey);
      if (!line) throw new Error('line is gone');

      const properties = { ...(line.properties || {}), [key]: value };
      const response = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, properties }),
      });
      if (!response.ok) throw new Error(String(response.status));

      if (shown) shown.textContent = value;
      setStatus(block, '');

      // Let the theme re-render from the changed cart. The line's key changes
      // with its properties, so the stale key held here has to be replaced by a
      // fresh render rather than patched.
      const event = new CustomEvent(CART_LINES_UPDATE_EVENT, { bubbles: true });
      event.action = 'update';
      event.promise = Promise.resolve({ detail: {} });
      document.dispatchEvent(event);
    } catch (error) {
      // Never leave a wrong size looking accepted: what is displayed here is
      // what gets made.
      setStatus(block, 'Could not update — please try again.');
    }
  }

  const pendantLengthValue = (cm) => `${Math.round(cm * 10) / 10} cm / ${Math.round((cm / 2.54) * 10) / 10} in`;

  async function applyPendantLength(block, cm) {
    const key = block.dataset.fsPendantKey;
    const lineKey = block.dataset.fsLineKey;
    if (!key || !lineKey) return;

    setStatus(block, 'Updating…');
    try {
      const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })).json();
      const line = cart.items.find((item) => item.key === lineKey);
      if (!line) throw new Error('line is gone');
      const properties = { ...(line.properties || {}) };
      if (cm === null) delete properties[key];
      else properties[key] = pendantLengthValue(cm);
      const response = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, properties }),
      });
      if (!response.ok) throw new Error(String(response.status));
      setStatus(block, '');
      const event = new CustomEvent(CART_LINES_UPDATE_EVENT, { bubbles: true });
      event.action = 'update';
      event.promise = Promise.resolve({ detail: {} });
      document.dispatchEvent(event);
    } catch (error) {
      setStatus(block, 'Could not update — please try again.');
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const pendantBlock = target.closest('[data-fs-cart-pendant]');
    const unitButton = target.closest('[data-fs-pendant-unit]');
    if (pendantBlock && unitButton instanceof HTMLButtonElement) {
      const input = pendantBlock.querySelector('[data-fs-pendant-custom-input]');
      if (!(input instanceof HTMLInputElement)) return;
      const previousUnit = input.dataset.fsPendantUnit || 'cm';
      const nextUnit = unitButton.dataset.fsPendantUnit || 'cm';
      const entered = Number(input.value);
      if (Number.isFinite(entered) && entered > 0 && previousUnit !== nextUnit) {
        const converted = previousUnit === 'cm' ? entered / 2.54 : entered * 2.54;
        input.value = String(Math.round(converted * 10) / 10);
      }
      input.dataset.fsPendantUnit = nextUnit;
      pendantBlock.querySelectorAll('[data-fs-pendant-unit]').forEach((button) => {
        const selected = button === unitButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      input.focus();
      return;
    }

    const toggle = target.closest('[data-fs-cart-size-toggle]');
    if (!toggle) return;

    const block = toggle.closest('[data-fs-cart-size]');
    const editor = block?.querySelector('[data-fs-cart-size-editor]');
    if (!editor) return;

    const opening = editor.hidden;
    editor.hidden = !opening;
    toggle.setAttribute('aria-expanded', String(opening));
    // Initialised on first open rather than up front: a cart of ten pieces should
    // not build ten size controls nobody has asked to see.
    if (opening && !editor.dataset.fsInitialised) {
      editor.dataset.fsInitialised = 'true';
      window.FSSizeSelectorInit?.(editor);
    }
  });

  document.addEventListener('fs:size-change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const block = target.closest('[data-fs-cart-size]');
    if (!block) return;
    applySize(block, event.detail?.value);
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const block = target.closest('[data-fs-cart-pendant]');
    if (!block) return;
    if (target.matches('[data-fs-pendant-custom-input]')) {
      if (!target.value.trim()) {
        applyPendantLength(block, null);
        return;
      }
      const entered = Number(target.value);
      if (!Number.isFinite(entered) || entered <= 0) {
        setStatus(block, 'Enter a valid length.');
        return;
      }
      const unit = target.dataset.fsPendantUnit || 'cm';
      const cm = unit === 'in' ? entered * 2.54 : entered;
      applyPendantLength(block, cm);
    }
    if (target.matches('[data-fs-pendant-default]')) {
      const input = block.querySelector('[data-fs-pendant-custom-input]');
      if (input instanceof HTMLInputElement) input.value = '';
      const panel = block.querySelector('[data-fs-pendant-custom-panel]');
      if (panel) panel.hidden = true;
      applyPendantLength(block, null);
      return;
    }
    if (target.matches('[data-fs-pendant-custom-choice]')) {
      const panel = block.querySelector('[data-fs-pendant-custom-panel]');
      if (panel) panel.hidden = false;
      block.querySelector('[data-fs-pendant-custom-input]')?.focus();
    }
  });
})();
